import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { keyframes } from "@mui/system";
import {
  Box,
  Typography,
  Rating,
  TextField,
  Switch,
  Button,
  Alert,
  Avatar,
  Stack,
  Container,
  Fade,
  Collapse,
  CircularProgress,
} from "@mui/material";
import { styled, useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import StarIcon from "@mui/icons-material/Star";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import callApi from "../../../../services/callApi";
import Loader from "../../../../components/Loader/Loader";

const MySwal = withReactContent(Swal);

// ─── Animación manito indicadora ─────────────────────────────────────────────
const handBounce = keyframes`
  0%   { transform: translateY(0px)  rotate(225deg);  }
  20%  { transform: translateY(-7px) rotate(213deg);  }
  40%  { transform: translateY(0px)  rotate(225deg);  }
  60%  { transform: translateY(-7px) rotate(237deg);  }
  80%  { transform: translateY(0px)  rotate(225deg);  }
  100% { transform: translateY(0px)  rotate(225deg);  }
`;

// ─── Tarea 2: Definición Estricta de 3 Tamaños de Letra (XS y MD) ────────────
const TYPE_SCALE = {
  L: { xs: "24px", md: "30px" },
  M: { xs: "16px", md: "18px" },
  S: { xs: "14px", md: "15px" },
};

const StyledTextField = styled(TextField)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    borderRadius: "12px",
    backgroundColor: "#f9fafb",
  },
}));

const StyledButton = styled(Button)(({ theme }) => ({
  borderRadius: "8px",
  padding: theme.spacing(1.5),
  textTransform: "none",
  fontFamily: "Poppins-SemiBold",
}));

const StyledRating = styled(Rating)(({ theme }) => ({
  fontSize: "3.5rem", // Tamaño visual del componente SVG, no texto leíble
  "& .MuiRating-iconFilled": {
    color: "#FFB400",
    filter: "drop-shadow(0px 2px 4px rgba(255, 180, 0, 0.4))",
  },
  "& .MuiRating-iconHover": {
    color: "#FFC400",
    transform: "scale(1.1)",
    transition: "transform 0.2s ease-in-out",
  },
  "& .MuiRating-iconEmpty": {
    color: "#e0e0e0",
  }
}));

// ─── Colores Pastel ──────────────────────────────────────────────────────────
const PASTEL_COLORS = [
  "#FFB3BA", "#BAFFC9", "#BAE1FF", "#E8BAFF", "#B3FFEC", "#C9B3FF", "#FFB3E6",
];

const getPastelColor = (letter) => {
  if (!letter) return PASTEL_COLORS[0];
  return PASTEL_COLORS[letter.toUpperCase().charCodeAt(0) % PASTEL_COLORS.length];
};

// ─── Rutina dinámica de TimeAgo ──────────────────────────────────────────────
const timeAgo = (dateStr) => {
  if (!dateStr) return "";
  const past = new Date(dateStr);
  const now = new Date();

  const diffTime = Math.abs(now - past);
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

  if (diffDays === 0) {
    if (past.getDate() === now.getDate()) {
      return "hoy";
    }
    return "hace 1 día";
  }

  if (diffDays < 7) {
    return diffDays === 1 ? "hace 1 día" : `hace ${diffDays} días`;
  }

  const diffWeeks = Math.floor(diffDays / 7);
  if (diffDays < 30) {
    return diffWeeks === 1 ? "hace 1 semana" : `hace ${diffWeeks} semanas`;
  }

  const diffMonths = Math.floor(diffDays / 30);
  if (diffMonths < 12) {
    return diffMonths === 1 ? "hace 1 mes" : `hace ${diffMonths} meses`;
  }

  const diffYears = Math.floor(diffDays / 365);
  return diffYears === 1 ? "hace 1 año" : `hace ${diffYears} años`;
};

const EvaluacionForm = () => {
  const { id } = useParams();
  const theme = useTheme();
  const isMobileViewport = useMediaQuery(theme.breakpoints.down('md'));
  const [evaluacionData, setEvaluacionData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [rating, setRating] = useState(null);
  const [opinion, setOpinion] = useState("");
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [time, setTimeoutStr] = useState("");
  const [hasExistingResponse, setHasExistingResponse] = useState(false);
  const [wasOriginallyAnonymous, setWasOriginallyAnonymous] = useState(false);
  const [id_profesional_fantasma, setIdProfesionalFantasma] = useState("");
  const [ratingError, setRatingError] = useState(false);
  const [isSubmittedSuccess, setIsSubmittedSuccess] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const fetchEvaluacion = async () => {
      setIsLoading(true);
      try {
        const url = `/agenda/evaluacionObtener?id_evaluacion_secreto=${id}`;
        const response = await callApi(url);
        setEvaluacionData(response);
        if (
          response.datos_profesional &&
          response.datos_profesional.id_profesional_fantasma
        ) {
          setIdProfesionalFantasma(
            response.datos_profesional.id_profesional_fantasma
          );
        }
        if (response.estado_respuesta) {
          setRating(response.datos_respuesta.nota || 0);
          setOpinion(response.datos_respuesta.comentario || "");
          setIsAnonymous(response.datos_respuesta.es_anonima || false);
          setWasOriginallyAnonymous(response.datos_respuesta.es_anonima || false);
          setTimeoutStr(response.datos_respuesta.timestamp);
          setHasExistingResponse(true);
        }
      } catch (error) {
        console.error("Error al obtener la información de evaluación:", error);
        setError("No se pudo cargar la información de evaluación.");
      } finally {
        setIsLoading(false);
      }
    };

    if (id) fetchEvaluacion();
  }, [id]);

  const handleRatingChange = (event, newValue) => {
    setRating(newValue);
    setRatingError(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (rating === 0 || rating === null) {
      setRatingError(true);
      MySwal.fire({
        title: "Se requiere una valoración",
        html: `
          <div style="background-color: #FFF9C4; color: #6D6542; border: 1px solid #F0E68C; border-radius: 4px; padding: 16px; text-align: left; margin-bottom: 10px;">
            <span style="font-family: 'Quicksand-Regular';">
              Debes seleccionar al menos una estrella para valorar tu experiencia.
            </span>
          </div>
        `,
        confirmButtonColor: "#8753b1",
        confirmButtonText: "Entendido",
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const url = "/agenda/evaluacionGuardar";
      const data = {
        id_evaluacion_secreto: id,
        id_profesional_fantasma: id_profesional_fantasma,
        nota: rating,
        comentario: opinion,
        es_anonima: isAnonymous,
      };

      const response = await callApi(url, "post", data);

      if (
        response &&
        (response.message === "Evaluación registrada exitosamente" ||
          response.message === "Evaluación editada exitosamente" ||
          response.statusCode === 200)
      ) {
        setIsSubmittedSuccess(true);
        setTimeoutStr(new Date().toISOString());
        setHasExistingResponse(true);
      } else {
        throw new Error("La respuesta de la API no fue la esperada");
      }
    } catch (error) {
      console.error("Error al enviar la evaluación:", error);
      MySwal.fire({
        title: "Error al guardar evaluación",
        html: `
          <div style="background-color: #fef2f2; color: #991b1b; border: 1px solid #fecaca; border-radius: 6px; padding: 16px; text-align: left;">
            <span style="font-family: 'Quicksand-Regular';">
              No se pudo procesar tu evaluación. Por favor intenta nuevamente en unos momentos.
            </span>
          </div>
        `,
        confirmButtonColor: "#d32f2f",
        confirmButtonText: "Intentar nuevamente",
        showCloseButton: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) return <Loader isLoading={isLoading} />;
  if (error) return <Alert severity="error">{error}</Alert>;
  if (!evaluacionData)
    return (
      <Alert severity="warning">
        No se encontró información de evaluación.
      </Alert>
    );

  const { datos_paciente, datos_profesional } = evaluacionData;

  const isButtonDisabled = rating === 0 || rating === null;
  const isReadOnlyAnon = hasExistingResponse && wasOriginallyAnonymous;
  const primerNombre = datos_profesional?.nombre ? datos_profesional.nombre.split(" ")[0] : "?";
  const primeraLetra = primerNombre[0].toUpperCase();
  const pastelColor = getPastelColor(primeraLetra);

  return (
    <Box sx={{ minHeight: "100vh", backgroundColor: "#fbfbfc", pt: { xs: 4, md: 8 }, pb: { xs: 12, md: 10 } }}>
      <Loader isLoading={isLoading} />

      <Container maxWidth="sm">
        {/* Tarjeta Principal Responsiva */}
        <Box
          sx={{
            width: "100%",
            p: { xs: "24px 20px", md: "48px 40px" },
            borderRadius: { xs: "16px", md: "24px" },
            background: "#ffffff",
            boxShadow: "0 2px 4px rgba(0,0,0,0.02), 0 8px 16px rgba(0,0,0,0.03), 0 16px 32px rgba(0,0,0,0.05)",
            border: "1px solid #eaeaea",
            boxSizing: "border-box",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          {/* Bloque Foto y Título Integrado */}
          <Stack
            direction="column"
            spacing={3}
            alignItems="center"
            justifyContent="center"
            sx={{ mb: { xs: 3, md: 5 }, width: "100%" }}
          >
            {/* Foto de Perfil Enclaustrada */}
            {datos_profesional?.foto_url ? (
              <Avatar
                src={datos_profesional.foto_url}
                alt={datos_profesional.nombre}
                variant="rounded"
                sx={{
                  width: { xs: 100, md: 180 },
                  height: { xs: 100, md: 180 },
                  borderRadius: { xs: "16px", md: "24px" },
                  boxShadow: "0px 8px 24px rgba(0,0,0,0.12)"
                }}
              />
            ) : (
              <Box
                sx={{
                  width: { xs: 100, md: 180 },
                  height: { xs: 100, md: 180 },
                  borderRadius: { xs: "16px", md: "24px" },
                  backgroundColor: pastelColor,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: "0px 8px 24px rgba(0,0,0,0.12)"
                }}
              >
                <Typography
                  sx={{
                    fontFamily: "Poppins-SemiBold",
                    fontSize: TYPE_SCALE.L,
                    transform: { xs: "scale(2)", md: "scale(2.2)" }, // Escala visual sin salirse del token
                    color: "#1c1c1c",
                    lineHeight: 1
                  }}
                >
                  {primeraLetra}
                </Typography>
              </Box>
            )}

            {/* Títulos Deslizantes */}
            <Box sx={{ width: "100%" }}>
              <Collapse in={!isSubmittedSuccess} timeout={600} unmountOnExit>
                <Box textAlign="center" sx={{ width: "100%", display: "flex", flexDirection: "column", alignItems: "center" }}>
                  <Typography
                    sx={{
                      fontFamily: "Poppins-SemiBold",
                      textAlign: "center",
                      color: "#1c1c1c",
                      fontSize: TYPE_SCALE.L,
                      maxWidth: "95%"
                    }}
                  >
                    ¿Cómo fue tu atención con {primerNombre}?
                  </Typography>

                  <Typography
                    sx={{
                      fontFamily: "Quicksand-Medium",
                      textAlign: "center",
                      color: "#5f6368",
                      fontSize: TYPE_SCALE.M,
                      maxWidth: "500px",
                      lineHeight: 1.5,
                      mt: 1
                    }}
                  >
                    {isReadOnlyAnon
                    ? <>Te invitamos a compartir tu experiencia. Tu opinión es muy valiosa y ayuda al {datos_profesional.profesion}, <strong style={{ color: "#1c1c1c" }}>{datos_profesional.nombre}</strong> a seguir mejorando.</>
                    : <>Hola <strong style={{ color: "#1c1c1c" }}>{datos_paciente.nombre}</strong>, te invitamos a compartir tu experiencia. Tu opinión es muy valiosa y ayuda al {datos_profesional.profesion}, <strong style={{ color: "#1c1c1c" }}>{datos_profesional.nombre}</strong> a seguir mejorando.</>
                  }
                  </Typography>
                </Box>
              </Collapse>

              <Collapse in={isSubmittedSuccess} timeout={800}>
                <Box textAlign="center" sx={{ width: "100%", pt: 2 }}>
                  <Typography sx={{ fontFamily: "Poppins-SemiBold", fontSize: TYPE_SCALE.L, color: "#1c1c1c" }}>
                    ¡Gracias por tu tiempo!
                  </Typography>
                  <Typography sx={{ fontFamily: "Quicksand-Medium", fontSize: TYPE_SCALE.M, color: "#5f6368", lineHeight: 1.5, mt: 2 }}>
                    Tus comentarios ya fueron registrados de forma segura y aportarán enormemente a seguir mejorando la atención.
                  </Typography>
                </Box>
              </Collapse>
            </Box>
          </Stack>

          {/* Formulario de Evaluación (Desliza hacia arriba al desaparecer) */}
          <Collapse in={!isSubmittedSuccess} timeout={700} unmountOnExit sx={{ width: "100%" }}>
            <Box component="form" id="evaluation-form" onSubmit={handleSubmit} sx={{ width: "100%" }}>

              {/* Sistema de Estrellas */}
              <Box
                my={3}
                sx={{
                  position: "relative",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  backgroundColor: "#fafafa",
                  borderRadius: "16px",
                  py: 3,
                  border: "1px solid #f0f0f0"
                }}
              >
                <Typography
                  sx={{ fontFamily: "Poppins-SemiBold", color: "#1c1c1c", mb: 2, fontSize: TYPE_SCALE.M }}
                >
                  Evaluación global
                </Typography>

                <Box sx={{ position: "relative", display: "inline-flex" }}>
                  <StyledRating
                    name="customized-color"
                    value={rating}
                    onChange={isReadOnlyAnon ? undefined : handleRatingChange}
                    readOnly={isReadOnlyAnon}
                    icon={<StarIcon fontSize="inherit" />}
                    emptyIcon={<StarIcon fontSize="inherit" />}
                  />
                  {!rating && !hasExistingResponse && (
                    <Box
                      sx={{
                        position: "absolute",
                        right: "0.2rem",
                        top: "-1.6rem",
                        fontSize: "1.6rem",
                        lineHeight: 1,
                        animation: `${handBounce} 0.85s ease-in-out infinite`,
                        userSelect: "none",
                        pointerEvents: "none",
                      }}
                    >
                      👆
                    </Box>
                  )}
                </Box>

                {rating > 0 && (
                  <Typography
                    sx={{
                      position: "absolute",
                      bottom: "12px",
                      right: "16px",
                      fontFamily: "Poppins-SemiBold",
                      color: "#ff8000",
                      fontSize: TYPE_SCALE.S,
                      textShadow: "0 0 8px rgba(255, 140, 0, 0.9), 0 0 18px rgba(255, 140, 0, 0.5), 0 0 30px rgba(255, 140, 0, 0.3)",
                    }}
                  >
                    {rating}/5
                  </Typography>
                )}

                {!rating && ratingError && (
                  <Typography
                    sx={{ fontFamily: "Quicksand-Medium", color: "#d32f2f", mt: 1.5, fontSize: TYPE_SCALE.S }}
                  >
                    Selecciona al menos 1 estrella
                  </Typography>
                )}
              </Box>

              {/* Input Comentarios */}
              <StyledTextField
                fullWidth
                id="opinion"
                label="Comentario"
                multiline
                rows={isMobileViewport ? 6 : 8}
                value={opinion}
                onChange={isReadOnlyAnon ? undefined : (e) => setOpinion(e.target.value)}
                placeholder="¿Quieres agregar algo más sobre la atención recibida? (Opcional)"
                inputProps={{ maxLength: 500 }}
                InputProps={{ readOnly: isReadOnlyAnon, sx: { fontSize: TYPE_SCALE.M, fontFamily: "Quicksand-Medium", fontWeight: 500 } }}
                InputLabelProps={{ sx: { fontSize: TYPE_SCALE.M, fontFamily: "Quicksand-Medium" } }}
                margin="normal"
                variant="outlined"
              />

              {/* Fila del Anónimo */}
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                my={3}
                sx={{
                  backgroundColor: "#f9fafb",
                  borderRadius: "12px",
                  padding: "16px 20px",
                  border: "1px solid #eaeaea"
                }}
              >
                <Box display="flex" alignItems="center" gap={2}>
                  <LockOutlinedIcon sx={{ color: isAnonymous ? "#1c1c1c" : "#9ca3af", fontSize: 24 }} />
                  <Box>
                    <Typography
                      sx={{ fontFamily: "Poppins-SemiBold", fontSize: TYPE_SCALE.M, color: "#1c1c1c" }}
                    >
                      Evaluación Anónima
                    </Typography>
                    <Typography
                      sx={{ fontFamily: "Quicksand-Medium", fontSize: TYPE_SCALE.S, color: "#6b7280" }}
                    >
                      {isAnonymous ? "Tu nombre se ocultará" : "Opcional"}
                    </Typography>
                  </Box>
                </Box>
                <Switch
                  checked={isAnonymous}
                  onChange={isReadOnlyAnon ? undefined : () => setIsAnonymous(!isAnonymous)}
                  disabled={isReadOnlyAnon}
                  color="primary"
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': { color: '#1877f2' },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#1877f2' },
                  }}
                />
              </Box>

              <Box mt={-1} mb={3} px={1}>
                <Typography
                  sx={{ fontFamily: "Quicksand-Medium", color: "#374151", lineHeight: 1.4, display: "block", textAlign: "left", fontSize: TYPE_SCALE.S }}
                >
                  {isAnonymous
                    ? "Tus respuestas se guardarán de forma anónima. El profesional y el público en general podrán ver tu respuesta sin tu nombre."
                    : "Tus respuestas se guardarán con tu nombre. El profesional y el público en general podrán ver tu respuesta con tu nombre."}
                </Typography>
              </Box>

              {/* Feedback Post-Envío / TimeAgo */}
              {hasExistingResponse && (
                <Box
                  sx={{
                    mb: 3,
                    backgroundColor: "#e8f0fe",
                    border: "1px solid #d2e3fc",
                    borderRadius: "10px",
                    p: 2,
                    textAlign: "center",
                    display: "flex",
                    flexDirection: "column",
                    gap: 0.5
                  }}
                >
                  <Typography
                    sx={{ fontFamily: "Poppins-SemiBold", fontSize: TYPE_SCALE.M, color: "#1877f2" }}
                  >
                    Evaluación enviada {timeAgo(time)}
                  </Typography>
                  {!isReadOnlyAnon && (
                    <Typography
                      sx={{ fontFamily: "Quicksand-Medium", fontSize: TYPE_SCALE.S, color: "#6b7280" }}
                    >
                      (Puedes modificar tu evaluación si lo deseas)
                    </Typography>
                  )}
                </Box>
              )}

            </Box>
          </Collapse>

          {/* Botón Normal y visible sólo en Desktop (MD+) */}
          <Box sx={{ display: { xs: 'none', md: isReadOnlyAnon ? 'none' : 'block' }, width: '100%', mt: isSubmittedSuccess ? 4 : 0 }}>
            <StyledButton
              type="submit"
              form="evaluation-form"
              variant="contained"
              fullWidth
              disabled={(isButtonDisabled && !isSubmittedSuccess) || isSubmitting}
              sx={{
                fontSize: TYPE_SCALE.M,
                backgroundColor: isSubmittedSuccess ? "#10b981" : (isButtonDisabled ? "#e0e0e0" : "#1877f2"),
                color: (isButtonDisabled && !isSubmittedSuccess) ? "#9e9e9e" : "white",
                py: 1.8,
                "&:hover": {
                  backgroundColor: isSubmittedSuccess ? "#059669" : (isButtonDisabled ? "#e0e0e0" : "#166fe5"),
                },
                boxShadow: isSubmittedSuccess ? "0px 6px 16px rgba(16, 185, 129, 0.25)" : (isButtonDisabled ? "none" : "0px 6px 16px rgba(24, 119, 242, 0.25)"),
                pointerEvents: isSubmittedSuccess ? "none" : "auto",
              }}
            >
              {isSubmittedSuccess ? "Evaluación enviada" : isSubmitting ? <CircularProgress size={24} color="inherit" /> : (hasExistingResponse ? "Actualizar evaluación" : "Enviar evaluación")}
            </StyledButton>
          </Box>

          {/* Botón Flotante Inferior visible sólo en Mobile (XS) */}
          <Box
            sx={{
              display: { xs: isReadOnlyAnon ? "none" : "flex", md: "none" },
              position: "fixed",
              bottom: 0,
              left: 0,
              right: 0,
              backgroundColor: "#ffffff",
              borderTop: "1px solid #eaeaea",
              px: 2,
              py: 2,
              zIndex: 1000,
              boxShadow: "0px -4px 12px rgba(0,0,0,0.05)"
            }}
          >
            <StyledButton
              type="submit"
              form="evaluation-form"
              variant="contained"
              fullWidth
              disabled={(isButtonDisabled && !isSubmittedSuccess) || isSubmitting}
              sx={{
                fontSize: TYPE_SCALE.M,
                backgroundColor: isSubmittedSuccess ? "#10b981" : (isButtonDisabled ? "#e0e0e0" : "#1877f2"),
                color: (isButtonDisabled && !isSubmittedSuccess) ? "#9e9e9e" : "white",
                py: 1.5,
                "&:hover": {
                  backgroundColor: isSubmittedSuccess ? "#059669" : (isButtonDisabled ? "#e0e0e0" : "#166fe5"),
                },
                boxShadow: "none",
                pointerEvents: isSubmittedSuccess ? "none" : "auto",
              }}
            >
              {isSubmittedSuccess ? "Evaluación enviada" : isSubmitting ? <CircularProgress size={24} color="inherit" /> : (hasExistingResponse ? "Actualizar evaluación" : "Enviar evaluación")}
            </StyledButton>
          </Box>

        </Box>
      </Container>
    </Box>
  );
};

export default EvaluacionForm;
